import { Component, OnInit } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { ApiService } from '../api.service';

@Component({
  selector: 'app-corporate-account',
  templateUrl: './corporate-account.component.html',
  styleUrls: ['./corporate-account.component.css']
})
export class CorporateAccountComponent implements OnInit {

  corporateForm!: FormGroup;
  isSubmitting: boolean = false;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private fb: FormBuilder,
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle("Corporate & Business Sales | Line Clear Express");
    this.metaService.updateTag(
      { name: "description", content: "Open a Corporate Account with Line Clear Express. Enjoy next-day delivery, express delivery, warehousing and more for your business." },
      "name=description"
    );

    this.corporateForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      contactName: ['', [Validators.required, Validators.minLength(2)]],
      contactNumber: ['', [Validators.required, Validators.pattern(/^[0-9\-\s\+]{7,15}$/)]],
      email: ['', [Validators.required, Validators.email]],
      officeLocation: ['', [Validators.required, Validators.minLength(5)]],
      nextDayDelivery: [false],
      perishableDelivery: [false],
      expressDelivery: [false],
      warehousing: [false],
      cartonDelivery: [false]
    });
  }

  isInvalid(fieldName: string): boolean {
    const field = this.corporateForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getError(fieldName: string): string {
    const field = this.corporateForm.get(fieldName);
    if (!field || !field.errors) return '';
    if (field.errors['required']) return 'This field is required';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters required`;
    if (field.errors['email']) return 'Invalid email format';
    if (field.errors['pattern']) return 'Invalid phone number format (e.g. 012-345 6789)';
    return 'Invalid value';
  }

  hasAnyService(): boolean {
    return this.corporateForm.value.nextDayDelivery || this.corporateForm.value.perishableDelivery || this.corporateForm.value.expressDelivery || this.corporateForm.value.warehousing || this.corporateForm.value.cartonDelivery;
  }

  onSubmit(): void {
    this.corporateForm.markAllAsTouched();

    if (this.corporateForm.invalid) {
      return;
    }

    if (!this.hasAnyService()) {
      return;
    }

    this.isSubmitting = true;

    const formData = this.corporateForm.value;

    // Build selected services list
    const services: string[] = [];
    if (formData.nextDayDelivery) services.push('Next-Day Delivery Guarantee');
    if (formData.perishableDelivery) services.push('Perishable Item Delivery');
    if (formData.expressDelivery) services.push('Express Delivery (1-3 Working Days)');
    if (formData.warehousing) services.push('Warehousing');
    if (formData.cartonDelivery) services.push('Carton Delivery');

    const payload = {
      companyName: formData.companyName,
      contactName: formData.contactName,
      contactNumber: formData.contactNumber,
      email: formData.email,
      officeLocation: formData.officeLocation,
      services: services.join(', ')
    };

    this.apiService.postCorporateInquiry(payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res.status === 'success') {
          this.resetForm();
          Swal.fire(
            'Inquiry Sent!',
            'Our corporate sales team will contact you shortly. Thank you!',
            'success'
          );
        } else {
          Swal.fire('Error', res.msg || 'Failed to send inquiry. Please try again.', 'error');
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Corporate inquiry error:', err);
        Swal.fire('Error', 'Something went wrong. Please try again later.', 'error');
      }
    });
  }

  resetForm(): void {
    this.corporateForm.reset();
    this.corporateForm.patchValue({
      nextDayDelivery: false,
      perishableDelivery: false,
      expressDelivery: false,
      warehousing: false,
      cartonDelivery: false
    });
  }
}
